import { Card, Typography } from 'antd'
import { format } from 'date-fns'
import { useSensorPayloadStrings } from 'pages/reporting/reports/rug/hooks/useSensorPayloadStrings'
import { RugReportSectionId } from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import { SignoffSection } from 'pages/reporting/shared/components/SignOffSection'
import { ReviewSection, ReviewSectionProps } from 'pages/reporting/shared/types'
import React, { useMemo } from 'react'
import styled from 'styled-components'

const Text = Typography.Text

const PageSection = styled(Card)`
  margin-bottom: 16px;
`

const SectionContent = styled.div`
  margin-left: 16px;
`

const DataRow = styled.div`
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
`

const formatDate = (date: string | Date | undefined) => {
  if (!date) return 'Not specified'

  try {
    if (typeof date === 'string') {
      return date // Assume it's already formatted
    }
    return format(date, 'MM/dd/yyyy')
  } catch {
    return 'Invalid date'
  }
}

const formatCalibrationTime = (timeString: string): string => {
  try {
    const date = new Date(timeString)
    if (isNaN(date.getTime())) return timeString
    return format(date, 'MM/dd/yyyy h:mm a')
  } catch {
    return timeString
  }
}

const RenderReact: React.FC<ReviewSectionProps> = ({ previewMode }) => {
  const {
    formData,
    handleSignoff,
    selectedRevisionData,
    isLoading,
    lockState,
    handleStartSignoff,
    handleUnlock,
    metadataEntryId,
    organization,
    unit,
    inspectionMetadata,
    formatMeasurement,
    selectedComponents,
  } = useRugReportOps()

  const selectedFormData = useMemo(
    () =>
      previewMode && selectedRevisionData ? selectedRevisionData : formData,
    [formData, selectedRevisionData, previewMode]
  )

  const {
    mainInfo,
    coverSectionData,
    summarySectionData,
    dataSheetSectionData,
    imagesSectionData,
    signoff,
    revisionMetadata,
  } = selectedFormData

  const supervisorSignDate = signoff?.supervisorSignoff?.timestamp
    ? new Date(signoff.supervisorSignoff.timestamp)
    : undefined

  const dataAnalystSignDate = signoff?.dataAnalystSignoff?.timestamp
    ? new Date(signoff.dataAnalystSignoff.timestamp)
    : undefined

  const reviewerSignDate = signoff?.reviewerSignoff?.timestamp
    ? new Date(signoff.reviewerSignoff.timestamp)
    : undefined

  const resolutionString = summarySectionData.inspectionResolution
    ? `${summarySectionData.inspectionResolution.width}" x ${summarySectionData.inspectionResolution.height}"`
    : 'Not specified'

  const deliverableResolutionString = summarySectionData.deliverableResolution
    ? `${summarySectionData.deliverableResolution.width}" x ${summarySectionData.deliverableResolution.height}"`
    : 'Not specified'

  const { payloadTypesStr, sensorFrequenciesStr, sensorDiametersStr } =
    useSensorPayloadStrings({
      payloads: dataSheetSectionData?.payloads,
      formatMeasurement,
    })

  const sensorAngle = inspectionMetadata?.sensorPayloadMetadata?.angle

  return (
    <div>
      <PageSection title='Asset Information'>
        <SectionContent>
          <DataRow>
            <Text strong>Customer:</Text>{' '}
            {mainInfo.customerName || organization?.data.name || 'N/A'}
          </DataRow>
          <DataRow>
            <Text strong>Location:</Text> {mainInfo.customerLocation || 'N/A'}
          </DataRow>
          <DataRow>
            <Text strong>Asset Name:</Text>{' '}
            {mainInfo.assetName || unit?.data.name || 'N/A'}
          </DataRow>
          <DataRow>
            <Text strong>Asset ID:</Text> {mainInfo.assetId}
          </DataRow>
          <DataRow>
            <Text strong>Asset Type:</Text> {mainInfo.assetType}
          </DataRow>
          <DataRow>
            <Text strong>Asset Material:</Text> {mainInfo.assetMaterial}
          </DataRow>
          <DataRow>
            <Text strong>Inspection Temperature:</Text>{' '}
            {mainInfo.inspectionTemperature}
          </DataRow>
          {selectedComponents &&
            selectedComponents.map((component) => {
              const axisLocations =
                mainInfo.componentAxisStartLocations?.[component.id]
              return (
                <React.Fragment key={component.id}>
                  <DataRow>
                    <Text strong>
                      X-Axis Start Location ({component.data.name}):
                    </Text>{' '}
                    {axisLocations?.xAxisStartLocation ?? 'Not specified'}
                  </DataRow>
                  <DataRow>
                    <Text strong>
                      Y-Axis Start Location ({component.data.name}):
                    </Text>{' '}
                    {axisLocations?.yAxisStartLocation ?? 'Not specified'}
                  </DataRow>
                </React.Fragment>
              )
            })}
        </SectionContent>
      </PageSection>

      <PageSection title='Inspection Details'>
        <SectionContent>
          <DataRow>
            <Text strong>Inspection Start Date:</Text>{' '}
            {formatDate(coverSectionData.inspectionStartDate)}
          </DataRow>
          <DataRow>
            <Text strong>Inspection End Date:</Text>{' '}
            {formatDate(coverSectionData.inspectionEndDate)}
          </DataRow>
          <DataRow>
            <Text strong>Revision Number:</Text>{' '}
            {revisionMetadata.revisionNumber}
          </DataRow>
          <DataRow>
            <Text strong>Publication Status:</Text>{' '}
            {revisionMetadata.isPublished ? 'Published' : 'Draft'}
          </DataRow>
        </SectionContent>
      </PageSection>

      <PageSection title='Inspection Summary'>
        <SectionContent>
          <DataRow>
            <Text strong>Coverage Scope:</Text>{' '}
            {summarySectionData.coverageScope || 'Not specified'}
          </DataRow>
          <DataRow>
            <Text strong>Inspection Resolution:</Text> {resolutionString}
          </DataRow>
          <DataRow>
            <Text strong>Deliverable Resolution:</Text>{' '}
            {deliverableResolutionString}
          </DataRow>
          <DataRow>
            <Text strong>Corrosion Summary:</Text>
          </DataRow>
          <DataRow style={{ marginLeft: 16 }}>
            {summarySectionData.corrosionBLUF ||
              'No corrosion summary provided'}
          </DataRow>
          {summarySectionData.inspectionSummaryNotes && (
            <>
              <DataRow>
                <Text strong>Additional Notes:</Text>
              </DataRow>
              <DataRow style={{ marginLeft: 16 }}>
                {summarySectionData.inspectionSummaryNotes}
              </DataRow>
            </>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Technical Details'>
        <SectionContent>
          <DataRow>
            <Text strong>Inspection Surface:</Text>{' '}
            {dataSheetSectionData.inspectionSurface || 'Not specified'}
          </DataRow>
          <DataRow>
            <Text strong>Surface Condition:</Text>{' '}
            {dataSheetSectionData.surfaceCondition || 'Not specified'}
          </DataRow>
          <DataRow>
            <Text strong>Coating Present:</Text>{' '}
            {dataSheetSectionData.coatingPresent ? 'Yes' : 'No'}
          </DataRow>

          {/* Display robot information */}
          {dataSheetSectionData.robots &&
            dataSheetSectionData.robots.length > 0 && (
              <>
                <DataRow style={{ marginTop: 16 }}>
                  <Text strong>Robots Used:</Text>
                </DataRow>
                {dataSheetSectionData.robots.map((robot, index) => (
                  <DataRow key={index} style={{ marginLeft: 16 }}>
                    Robot {index + 1}: {robot.model || 'Unknown Model'} (S/N:{' '}
                    {robot.serialNumber})
                  </DataRow>
                ))}
              </>
            )}

          {/* Display calibration blocks */}
          {dataSheetSectionData.calibrationBlocks &&
            dataSheetSectionData.calibrationBlocks.length > 0 && (
              <>
                <DataRow style={{ marginTop: 16 }}>
                  <Text strong>Calibration Blocks:</Text>
                </DataRow>
                {dataSheetSectionData.calibrationBlocks.map((block, idx) => (
                  <DataRow key={idx} style={{ marginLeft: 16 }}>
                    {block.type || 'N/A'} - S/N: {block.serialNumber || 'N/A'}
                  </DataRow>
                ))}
              </>
            )}

          {/* Display calibration times */}
          {dataSheetSectionData.calibrationTimes &&
            dataSheetSectionData.calibrationTimes.length > 0 && (
              <DataRow style={{ marginTop: 8, marginLeft: 16 }}>
                Calibration Times:{' '}
                {dataSheetSectionData.calibrationTimes
                  .map(formatCalibrationTime)
                  .join(', ')}
              </DataRow>
            )}

          {/* Display calibration steps */}
          {dataSheetSectionData.calibrationSteps &&
            dataSheetSectionData.calibrationSteps.length > 0 && (
              <>
                <DataRow style={{ marginTop: 16 }}>
                  <Text strong>Calibration Steps:</Text>
                </DataRow>
                {dataSheetSectionData.calibrationSteps.map((step, index) => (
                  <DataRow key={index} style={{ marginLeft: 16 }}>
                    {step.stepName}: {step.distance.value} {step.distance.unit}
                  </DataRow>
                ))}
              </>
            )}

          {/* Display sensor serial numbers */}
          {dataSheetSectionData.sensorSerialNumbers &&
            Object.keys(dataSheetSectionData.sensorSerialNumbers).length >
              0 && (
              <>
                <DataRow style={{ marginTop: 16 }}>
                  <Text strong>Sensor Serial Numbers:</Text>
                </DataRow>
                {Object.entries(dataSheetSectionData.sensorSerialNumbers).map(
                  ([key, serials]) => (
                    <DataRow key={key} style={{ marginLeft: 16 }}>
                      Sensor {key}: {serials.join(', ')}
                    </DataRow>
                  )
                )}
              </>
            )}
        </SectionContent>
      </PageSection>

      <PageSection title='Metadata'>
        <SectionContent>
          {inspectionMetadata?.inspectionSoftwareVersion && (
            <DataRow>
              <Text strong>Inspection Software Version:</Text>{' '}
              {inspectionMetadata.inspectionSoftwareVersion}
            </DataRow>
          )}
          {inspectionMetadata?.reported_ascan_count !== undefined && (
            <DataRow>
              <Text strong>A-Scan Count:</Text>{' '}
              {inspectionMetadata.reported_ascan_count.toLocaleString()}
            </DataRow>
          )}
          {payloadTypesStr && (
            <DataRow>
              <Text strong>Sensor Payload Type:</Text> {payloadTypesStr}
            </DataRow>
          )}
          {sensorFrequenciesStr && (
            <DataRow>
              <Text strong>Sensor Frequency:</Text> {sensorFrequenciesStr}
            </DataRow>
          )}
          {sensorDiametersStr && (
            <DataRow>
              <Text strong>Sensor Diameter:</Text> {sensorDiametersStr}
            </DataRow>
          )}
          {sensorAngle !== undefined && (
            <DataRow>
              <Text strong>Sensor Angle:</Text> {sensorAngle}°
            </DataRow>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Selected Images'>
        <SectionContent>
          {imagesSectionData.imageReportIds &&
          imagesSectionData.imageReportIds.length > 0 ? (
            <DataRow>
              <Text strong>Number of Images:</Text>{' '}
              {imagesSectionData.imageReportIds.length}
            </DataRow>
          ) : (
            <DataRow>
              <Text type='secondary'>No images selected</Text>
            </DataRow>
          )}
        </SectionContent>
      </PageSection>

      {/* Display supervisor, data analyst, and reviewer information from signoff */}
      {(signoff?.supervisorSignoff ||
        signoff?.dataAnalystSignoff ||
        signoff?.reviewerSignoff) && (
        <PageSection title='Signoff Information'>
          <SectionContent>
            {signoff?.supervisorSignoff && (
              <>
                <DataRow>
                  <Text strong>Inspection Supervisor:</Text>{' '}
                  {signoff.supervisorSignoff.name}
                </DataRow>
                <DataRow>
                  <Text strong>Supervisor Qualifications:</Text>{' '}
                  {signoff.supervisorSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text strong>Supervisor Signoff Date:</Text>{' '}
                  {formatDate(supervisorSignDate)}
                </DataRow>
              </>
            )}

            {signoff?.dataAnalystSignoff && (
              <>
                <DataRow
                  style={{ marginTop: signoff?.supervisorSignoff ? 16 : 0 }}>
                  <Text strong>Data Analyst:</Text>{' '}
                  {signoff.dataAnalystSignoff.name}
                </DataRow>
                <DataRow>
                  <Text strong>Data Analyst Qualifications:</Text>{' '}
                  {signoff.dataAnalystSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text strong>Data Analyst Signoff Date:</Text>{' '}
                  {formatDate(dataAnalystSignDate)}
                </DataRow>
              </>
            )}

            {signoff?.reviewerSignoff && (
              <>
                <DataRow
                  style={{
                    marginTop:
                      signoff?.supervisorSignoff || signoff?.dataAnalystSignoff
                        ? 16
                        : 0,
                  }}>
                  <Text strong>Reviewer:</Text> {signoff.reviewerSignoff.name}
                </DataRow>
                <DataRow>
                  <Text strong>Reviewer Qualifications:</Text>{' '}
                  {signoff.reviewerSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text strong>Review Date:</Text>{' '}
                  {formatDate(reviewerSignDate)}
                </DataRow>
              </>
            )}
          </SectionContent>
        </PageSection>
      )}

      {/* Only show SignoffSection if not in preview mode */}
      {!previewMode && (
        <SignoffSection
          currentSignoff={signoff}
          onSignoff={handleSignoff}
          loading={isLoading}
          isLocked={lockState.isLocked}
          onStartSignoff={handleStartSignoff}
          onUnlock={handleUnlock}
          publishedPdfPath={formData.publishedPdfPath}
          metadataEntryId={metadataEntryId}
        />
      )}

      {/* If in preview mode and has signoff, show detailed read-only signoff info */}
      {previewMode && signoff && (
        <PageSection title='Signoff Details'>
          <SectionContent>
            {signoff.supervisorSignoff && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Supervisor Signoff</Text>
                <DataRow>
                  <Text>Name:</Text> {signoff.supervisorSignoff.name}
                </DataRow>
                <DataRow>
                  <Text>Email:</Text> {signoff.supervisorSignoff.email}
                </DataRow>
                <DataRow>
                  <Text>Qualifications:</Text>{' '}
                  {signoff.supervisorSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text>Date:</Text>{' '}
                  {format(
                    new Date(signoff.supervisorSignoff.timestamp),
                    'MMM dd, yyyy HH:mm:ss'
                  )}
                </DataRow>
              </div>
            )}
            {signoff.dataAnalystSignoff && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Data Analyst Signoff</Text>
                <DataRow>
                  <Text>Name:</Text> {signoff.dataAnalystSignoff.name}
                </DataRow>
                <DataRow>
                  <Text>Email:</Text> {signoff.dataAnalystSignoff.email}
                </DataRow>
                <DataRow>
                  <Text>Qualifications:</Text>{' '}
                  {signoff.dataAnalystSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text>Date:</Text>{' '}
                  {format(
                    new Date(signoff.dataAnalystSignoff.timestamp),
                    'MMM dd, yyyy HH:mm:ss'
                  )}
                </DataRow>
              </div>
            )}
            {signoff.reviewerSignoff && (
              <div>
                <Text strong>Reviewer Signoff</Text>
                <DataRow>
                  <Text>Name:</Text> {signoff.reviewerSignoff.name}
                </DataRow>
                <DataRow>
                  <Text>Email:</Text> {signoff.reviewerSignoff.email}
                </DataRow>
                <DataRow>
                  <Text>Qualifications:</Text>{' '}
                  {signoff.reviewerSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text>Date:</Text>{' '}
                  {format(
                    new Date(signoff.reviewerSignoff.timestamp),
                    'MMM dd, yyyy HH:mm:ss'
                  )}
                </DataRow>
              </div>
            )}
          </SectionContent>
        </PageSection>
      )}
    </div>
  )
}

export const ReportReview: ReviewSection = {
  id: RugReportSectionId.REVIEW,
  title: 'Review',
  description: 'Review report data and complete sign off',
  RenderReact,
  required: true,
}
